import React, { useState, useRef, useEffect } from "react";
import homeSection03Card01 from "@/shared/assets/img/home-sec03-card01.png";
import homeSection03Card02 from "@/shared/assets/img/home-sec03-card02.png";
import homeSection03Card03 from "@/shared/assets/img/home-sec03-card03.png";
import homeSection03Card04 from "@/shared/assets/img/home-sec03-card04.png";
import homeSection03Card05 from "@/shared/assets/img/home-sec03-card05.png";

/**
 * Section3 - Parent Reviews Carousel (부모 후기)
 * Displays testimonials in an infinite carousel
 */
export const Section3: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(1); // Start at 1 (actual first slide)
  const [isTransitioning, setIsTransitioning] = useState(true);
  const autoPlayRef = useRef<NodeJS.Timeout>();
  const carouselRef = useRef<HTMLDivElement>(null);

  const features = [
    {
      id: 1,
      image: homeSection03Card01,
      title: "난이도별 도안으로\n성취감도 느끼고 집중력도 높아져요",
      description:
        "교구로 다양하게 만든 다음 학습콘텐츠를 이용하면 집중력이 오래가는 것 같아요",
    },
    {
      id: 2,
      image: homeSection03Card02,
      title: "다양한 구성의 콘텐츠로\n아이가 재밌어해요",
      description:
        "알파벳 하나에도 다양한 유닛으로 반복 학습을 하니 금방 익히는 것 같아요",
    },
    {
      id: 3,
      image: homeSection03Card03,
      title: "숫자가 약한 아이의 맞춤\n추천해줘 너무 좋네요",
      description:
        "활용 데이터 분석을 통해 아이 맞춤 콘텐츠를 추천해 주니 골고루 발달할 수 있을 것 같아요",
    },
    {
      id: 4,
      image: homeSection03Card04,
      title: "자석블럭 도안으로\n놀이가 더 다양해졌어요",
      description:
        "도안 따라 만들며 아이가 새로운 모양을 스스로 만들어보려 할 만큼 놀이가 풍부해졌어요.",
    },
    {
      id: 5,
      image: homeSection03Card05,
      title: "아이랑 놀면서\n배우기 딱 좋아요.",
      description: "단계별로 진도가 보이니까 저도 아이도 만족도가 높아요.",
    },
  ];

  // Create infinite carousel array: [last, ...features, first]
  const infiniteFeatures = [
    features[features.length - 1],
    ...features,
    features[0],
  ];

  // Auto-play infinite carousel
  useEffect(() => {
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(autoPlayRef.current);
  }, []);

  // Handle infinite loop - reset position when reaching clones
  useEffect(() => {
    const transitionTimer = setTimeout(() => {
      // When we reach the clone of the last slide (at end), jump to actual first slide
      if (currentIndex === infiniteFeatures.length - 1) {
        setIsTransitioning(false);
        setCurrentIndex(1);
      }
      // When we reach the clone of the first slide (at start), jump to actual last slide
      else if (currentIndex === 0) {
        setIsTransitioning(false);
        setCurrentIndex(features.length);
      } else {
        setIsTransitioning(true);
      }
    }, 0);

    return () => clearTimeout(transitionTimer);
  }, [currentIndex, infiniteFeatures.length, features.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev + 1);
  };

  return (
    <section className="w-full bg-white py-16 lg:py-24 overflow-hidden text-center">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header Section */}
        <div className="mb-16 lg:mb-20 text-center">
          <p className="text-lg text-gray-600 font-normal mb-4 font-['Pretendard']">
            다른 부모님들은 이렇게 느꼈어요!
          </p>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-black font-['NanumSquareRoundOTF'] leading-tight">
            생생한 후기를 확인해 보세요
          </h2>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Carousel Wrapper */}
          <div
            ref={carouselRef}
            className="flex gap-4 lg:gap-6 overflow-hidden"
          >
            {/* Render infinite carousel */}
            {infiniteFeatures.map((feature, index) => (
              <div
                key={`${feature.id}-${index}`}
                className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0"
                style={{
                  transform: `translateX(calc(-${currentIndex * (100 / 3)}% - ${
                    currentIndex * 1.5
                  }rem))`,
                  transition: "transform 500ms ease-out",
                }}
              >
                {/* Card */}
                <div className="rounded-r-3xl overflow-hidden bg-white border border-gray-200 hover:shadow-lg transition-shadow flex flex-col h-96 lg:h-[500px] relative">
                  {/* Background Image - Full Coverage */}
                  <div className="absolute inset-0 w-full h-full">
                    {feature.image ? (
                      <img
                        src={feature.image}
                        alt={`Feature ${feature.id}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-300"></div>
                    )}
                  </div>

                  {/* Content Container - Overlaid at bottom */}
                  <div className="relative z-10 mt-auto bg-orange-500 rounded-tl-[50px] rounded-br-3xl p-6 lg:p-8 flex flex-col justify-center gap-4">
                    {/* Title */}
                    <h3 className="text-xl lg:text-2xl font-extrabold text-white font-['NanumSquareRoundOTF'] leading-tight whitespace-pre-line">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm lg:text-base font-medium text-white font-['Pretendard'] leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-0 lg:-left-16 top-1/2 transform -translate-y-1/2 z-10 bg-white hover:bg-gray-100 rounded-full p-3 lg:p-4 transition-colors shadow-md"
            aria-label="Previous review"
          >
            <svg
              className="w-6 h-6 lg:w-8 lg:h-8 text-gray-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 lg:-right-16 top-1/2 transform -translate-y-1/2 z-10 bg-white hover:bg-gray-100 rounded-full p-3 lg:p-4 transition-colors shadow-md"
            aria-label="Next review"
          >
            <svg
              className="w-6 h-6 lg:w-8 lg:h-8 text-gray-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center items-center gap-3 mt-12 lg:mt-16">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index + 1)}
              className={`h-3 rounded-full transition-all duration-300 ${
                index === (currentIndex - 1 + features.length) % features.length
                  ? "bg-orange-500 w-8"
                  : "bg-gray-300 w-3 hover:bg-gray-400"
              }`}
              aria-label={`Go to review ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
